import { Icon, IconSize, IconVariants, Text as SpiceText, TextVariants } from "@next-platform/client-common-spicy-ui";
import { hideNotification, NotificationType, useAppDispatch, useAppSelector } from "@next-platform/client-common-core";
import { Tooltip, TooltipProps } from "@nextui-org/react";
import React, { useCallback, useEffect, useMemo } from "react";

import { cn } from "@/utils";

enum NotificationColor {
  DEFAULT = "default",
  PRIMARY = "primary",
  SECONDARY = "secondary",
  SUCCESS = "success",
  WARNING = "warning",
  DANGER = "danger",
  FOREGROUND = "foreground",
}

const tooltipStyle: TooltipProps["style"] = { zIndex: 999999999 };

const Notification = () => {
  const {
    isVisible,
    message: notificationMessage,
    title: notificationTitle,
    type: notificationType,
    duration: notificationDuration,
  } = useAppSelector((state) => state.notification);
  let className = "";
  const dispatch = useAppDispatch();

  let color = NotificationColor.DEFAULT;
  switch (notificationType) {
    case NotificationType.success:
      color = NotificationColor.SUCCESS;
      break;

    case NotificationType.warning:
      color = NotificationColor.WARNING;
      break;

    case NotificationType.error:
      className = "bg-red-9";
      break;
    default:
      color = NotificationColor.PRIMARY;
      className = "bg-blue-10";
      break;
  }

  const notificationClassNames: TooltipProps["classNames"] = useMemo(
    () => ({
      base: "fixed w-full top-2 left-0 flex justify-center cursor-pointer z-50",
      content: cn("font-medium w-max p-2 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl rounded-xl", className),
    }),
    [className],
  );

  const onClose = useCallback((): void => {
    dispatch(hideNotification());
  }, [dispatch]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (isVisible) {
      timer = setTimeout(() => {
        onClose();
      }, notificationDuration);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [notificationDuration, isVisible, onClose]);

  return (
    <Tooltip
      style={tooltipStyle}
      isOpen={isVisible}
      className="flex"
      classNames={notificationClassNames}
      content={
        <div className="flex flex-col items-center gap-0.5" onClick={onClose}>
          {notificationTitle ? (
            <SpiceText variant={TextVariants.LABEL_MEDIUM_BOLD} className="whitespace-normal break-words text-center text-white">
              {notificationTitle}
            </SpiceText>
          ) : null}
          <SpiceText variant={TextVariants.LABEL_MEDIUM_MEDIUM} className="whitespace-normal break-words text-center text-white">
            {notificationMessage} <Icon icon={IconVariants.CLOSE} className="relative top-0.5 ml-1" size={IconSize.XSMALL} />
          </SpiceText>
        </div>
      }
      placement="bottom"
      color={color}
    >
      <div />
    </Tooltip>
  );
};

export default Notification;
